/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, ListSubheader, Menu, MenuItem } from '@mui/material';
import { clamp } from 'lodash';
import React from 'react';
import { CustomIconButton, dateUtils } from '@opengeoweb/shared';
import { Options } from '@opengeoweb/theme';
import { getTimelineRange } from './getTimelineRange';
import { useBlockDrag } from './useBlockDrag';
import { useModifierKeyHeld } from './useModifierKeyHeld';
import { WarningEventTimelineBlockControls } from './WarningEventTimelineBlockControls';
import type { DragMode } from './warningEventTimelineDragUtils';
import {
  ABSOLUTE_MAX_DAY,
  EDGE_HIT_ZONE_PX,
  EXPANDED_BLOCK_HEIGHT,
  EXPANDED_BLOCK_TOP,
  formatOverflowHours,
} from './warningEventTimelineDragUtils';
import { PublicWarningStatus } from '../../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../../utils/i18n';
import {
  LevelColors,
  levelOptions,
  WarningLevel,
} from '../../../PublicWarningsForm/LevelField';
import { getBlockStyle } from '../../../WarningsTimeline/warningBlockColors';
import {
  EDGE_PADDING,
  getTimelineX,
} from '../../../WarningsTimeline/warningsTimelineUtils';

export interface TimelineEditing {
  onTimesChange: (validFrom: string, validUntil: string) => void;
  onLevelChange?: (level: WarningLevel) => void;
}

interface WarningEventTimelineBlockProps {
  start: Date;
  end: Date;
  now: Date;
  drawableWidth: number;
  validFrom: string;
  validUntil: string;
  level: string;
  status?: PublicWarningStatus;
  isCompact?: boolean;
  editing?: TimelineEditing;
}

const LABELS_INLINE_MIN_WIDTH = 40;
const COMPACT_BLOCK_BOTTOM_GAP = 4;
const OVERFLOW_LABEL_COLLISION_PX = 40;
const OVERFLOW_STRIP_WIDTH = 15;
const KEBAB_CLICK_TOLERANCE_PX = 4;

const STRIPED_BLOCK_BACKGROUNDS: Record<WarningLevel, string> = {
  [WarningLevel.moderate]: '#FFFACE',
  [WarningLevel.severe]: '#FADDC2',
  [WarningLevel.extreme]: '#F1D0D0',
};

// SVG pattern: uniform stripe width, unlike CSS gradients on subpixel boundaries
const stripedBlockBackground = (level: WarningLevel | null): string => {
  const stripe = encodeURIComponent(
    level ? LevelColors[level].color : '#AAAAAA',
  );
  const backgroundColor = level ? STRIPED_BLOCK_BACKGROUNDS[level] : '#FFFFFF';
  const svg = `%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpath d='M-3 3l6-6M0 12L12 0M9 15l6-6' stroke='${stripe}' stroke-width='1'/%3E%3C/svg%3E`;
  return `url("data:image/svg+xml,${svg}"), ${backgroundColor}`;
};

const EXPANDED_BLOCK_POSITION_SX = {
  top: EXPANDED_BLOCK_TOP,
  height: EXPANDED_BLOCK_HEIGHT,
};
const COMPACT_BLOCK_POSITION_SX = { top: 0, bottom: COMPACT_BLOCK_BOTTOM_GAP };

const INLINE_LABEL_TOP = EXPANDED_BLOCK_TOP - 14;
const STAGGERED_FROM_LABEL_TOP = EXPANDED_BLOCK_TOP - 12;
const STAGGERED_UNTIL_LABEL_TOP = EXPANDED_BLOCK_TOP - 24;

const LABEL_OVERLAY_SX = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  fontSize: 11,
  color: 'geowebColors.typographyAndIcons.text',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  zIndex: 3,
} as const;

const OVERFLOW_LABEL_SX = {
  position: 'absolute',
  top: INLINE_LABEL_TOP,
  fontSize: 11,
  color: 'geowebColors.typographyAndIcons.text',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
} as const;

const EDGE_HANDLE_SX = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: EDGE_HIT_ZONE_PX,
  cursor: 'col-resize',
  zIndex: 2,
} as const;

const PAST_FILL_SX = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  backgroundColor: 'geowebColors.greys.accessibleLight',
} as const;

const formatTime = (date: Date): string =>
  date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

const TimeLabel: React.FC<{ time: Date; top: number; x: number }> = ({
  time,
  top,
  x,
}) => (
  <Box
    component="span"
    style={{ left: x }}
    sx={{ position: 'absolute', top, transform: 'translateX(-50%)' }}
  >
    {formatTime(time)}
  </Box>
);

const EdgeHandle: React.FC<{
  edge: 'start' | 'end';
  onBegin: (mode: DragMode, event: React.PointerEvent) => void;
}> = ({ edge, onBegin }) => (
  <Box
    data-testid={`warning-event-block-${edge}-handle`}
    onPointerDown={(event): void => {
      event.stopPropagation();
      onBegin(edge, event);
    }}
    sx={{
      ...EDGE_HANDLE_SX,
      ...(edge === 'start' ? { left: 0 } : { right: 0 }),
    }}
  />
);

export const WarningEventTimelineBlock: React.FC<
  WarningEventTimelineBlockProps
> = ({
  start,
  end,
  now,
  drawableWidth,
  validFrom,
  validUntil,
  level,
  status,
  isCompact = false,
  editing,
}) => {
  const parsedFrom = React.useMemo(
    () => dateUtils.isoStringToDate(validFrom, false) ?? null,
    [validFrom],
  );
  const parsedUntil = React.useMemo(
    () => dateUtils.isoStringToDate(validUntil, false) ?? null,
    [validUntil],
  );

  const { t } = useWarningsTranslation();
  const isEditable = Boolean(editing) && !isCompact;
  const showEditAffordances = useModifierKeyHeld(isEditable);
  const absoluteMaxEnd = getTimelineRange([0, ABSOLUTE_MAX_DAY], now).end;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const isMenuOpen = Boolean(menuAnchorEl);
  const kebabRef = React.useRef<HTMLDivElement | null>(null);
  const kebabPressRef = React.useRef<{ x: number; y: number } | null>(null);

  const {
    dragPreview,
    dragMode,
    beginDrag,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  } = useBlockDrag({
    from: parsedFrom,
    until: parsedUntil,
    rangeStart: start,
    rangeEnd: end,
    now,
    maxUntil: absoluteMaxEnd,
    drawableWidth,
    onTimesChange: isEditable ? editing?.onTimesChange : undefined,
  });

  const from = dragPreview?.from ?? parsedFrom;
  const until = dragPreview?.until ?? parsedUntil;

  if (!from || !until) {
    return null;
  }

  const minX = EDGE_PADDING;
  const maxX = EDGE_PADDING + drawableWidth;
  const left = clamp(getTimelineX(from, start, end, drawableWidth), minX, maxX);
  const right = clamp(
    getTimelineX(until, start, end, drawableWidth),
    minX,
    maxX,
  );
  const width = Math.max(0, right - left);
  const isBlockVisible = width > 0;

  const overflowLeftMs = Math.max(0, start.getTime() - from.getTime());
  const overflowRightMs = Math.max(0, until.getTime() - end.getTime());

  // unsaved and draft warnings render in the faded striped style; only published warnings are filled
  const isStriped =
    !status ||
    status === PublicWarningStatus.DRAFT ||
    status === PublicWarningStatus.DRAFT_PUBLISHED;
  const parsedLevel = (level as WarningLevel) || null;
  const { background: solidBackground, borderColor } = getBlockStyle(
    parsedLevel,
    status,
    false,
  );
  const background = isStriped
    ? stripedBlockBackground(parsedLevel)
    : solidBackground;

  // The part of the block before "now" is rendered grey;
  const nowX = clamp(getTimelineX(now, start, end, drawableWidth), left, right);
  const pastPercentage = isBlockVisible ? ((nowX - left) / width) * 100 : 0;

  const staggerLabels = width < LABELS_INLINE_MIN_WIDTH;
  const showFromLabel = overflowLeftMs === 0;
  const showUntilLabel = overflowRightMs === 0;
  const blockPositionSx = isCompact
    ? COMPACT_BLOCK_POSITION_SX
    : EXPANDED_BLOCK_POSITION_SX;
  const rightOverflowLabelCollides =
    showFromLabel && maxX - left < OVERFLOW_LABEL_COLLISION_PX;

  // a press on the kebab still starts a block drag; it only opens the menu when the pointer barely moved
  const onBlockPointerUp = (event: React.PointerEvent): void => {
    const press = kebabPressRef.current;
    kebabPressRef.current = null;
    onPointerUp(event);
    if (
      press &&
      Math.abs(event.clientX - press.x) < KEBAB_CLICK_TOLERANCE_PX &&
      Math.abs(event.clientY - press.y) < KEBAB_CLICK_TOLERANCE_PX
    ) {
      setMenuAnchorEl(kebabRef.current);
    }
  };

  const blockRadiusSx = {
    borderRadius: '4px',
    ...(overflowLeftMs > 0
      ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }
      : {}),
    ...(overflowRightMs > 0
      ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
      : {}),
  };

  const overflows = [
    {
      side: 'left',
      overflowMs: overflowLeftMs,
      sign: '-',
      isPast: now.getTime() >= start.getTime(),
      stripSx: { left: EDGE_PADDING - OVERFLOW_STRIP_WIDTH },
      labelSx: { left: 2 },
    },
    {
      side: 'right',
      overflowMs: overflowRightMs,
      sign: '+',
      isPast: now.getTime() >= until.getTime(),
      stripSx: { left: maxX },
      labelSx: {
        right: 2,
        ...(rightOverflowLabelCollides
          ? { top: STAGGERED_UNTIL_LABEL_TOP }
          : {}),
      },
    },
    // a strip stays mounted while dragging so it keeps the pointer capture once its overflow is gone
  ].filter((overflow) => overflow.overflowMs > 0 || dragMode !== null);

  return (
    <>
      {isBlockVisible && !isCompact ? (
        <Box
          data-layout={staggerLabels ? 'staggered' : 'inline'}
          data-testid="warning-event-block-labels"
          sx={LABEL_OVERLAY_SX}
        >
          {showFromLabel ? (
            <TimeLabel
              time={from}
              top={staggerLabels ? STAGGERED_FROM_LABEL_TOP : INLINE_LABEL_TOP}
              x={left}
            />
          ) : null}
          {showUntilLabel ? (
            <TimeLabel
              time={until}
              top={staggerLabels ? STAGGERED_UNTIL_LABEL_TOP : INLINE_LABEL_TOP}
              x={left + width}
            />
          ) : null}
        </Box>
      ) : null}
      {overflows.map(({ side, overflowMs, sign, isPast, stripSx, labelSx }) => (
        <React.Fragment key={side}>
          <Box
            data-testid={`warning-event-block-overflow-${side}`}
            onPointerCancel={onPointerCancel}
            onPointerDown={
              isEditable ? (event): void => beginDrag('move', event) : undefined
            }
            onPointerMove={onPointerMove}
            onPointerUp={onBlockPointerUp}
            sx={{
              position: 'absolute',
              ...blockPositionSx,
              width: OVERFLOW_STRIP_WIDTH,
              opacity: 0.4,
              visibility: overflowMs > 0 ? 'visible' : 'hidden',
              ...(isPast
                ? { backgroundColor: 'geowebColors.greys.accessibleLight' }
                : { background }),
              ...(isEditable
                ? {
                    cursor: dragMode === 'move' ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    userSelect: 'none',
                  }
                : {}),
              ...stripSx,
            }}
          />
          {!isCompact && overflowMs > 0 ? (
            <Box
              component="span"
              data-testid={`warning-event-block-overflow-${side}-label`}
              sx={{ ...OVERFLOW_LABEL_SX, ...labelSx }}
            >
              {`${sign}${formatOverflowHours(overflowMs)}`}
            </Box>
          ) : null}
        </React.Fragment>
      ))}
      {isBlockVisible ? (
        <Box
          data-testid="warning-event-block"
          onPointerCancel={onPointerCancel}
          onPointerDown={
            isEditable ? (event): void => beginDrag('move', event) : undefined
          }
          onPointerMove={onPointerMove}
          onPointerUp={onBlockPointerUp}
          style={{ left, width }}
          sx={{
            position: 'absolute',
            ...blockPositionSx,
            ...(isStriped
              ? { border: 0 }
              : {
                  border: '2px solid',
                  borderColor:
                    status === PublicWarningStatus.EXPIRED ||
                    status === PublicWarningStatus.WITHDRAWN
                      ? borderColor
                      : background,
                }),
            ...blockRadiusSx,
            ...(isEditable
              ? {
                  cursor: dragMode === 'move' ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  userSelect: 'none',
                  '&:hover': {
                    border: '2px dashed',
                    borderColor: 'primary.main',
                  },
                  '&:hover .warning-event-block-kebab': {
                    visibility: 'visible',
                  },
                }
              : {}),
            ...(showEditAffordances
              ? { border: '2px dashed', borderColor: 'primary.main' }
              : {}),
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              ...blockRadiusSx,
            }}
          >
            <Box
              data-testid="warning-event-block-past"
              style={{ width: `${pastPercentage}%` }}
              sx={PAST_FILL_SX}
            />
            <Box
              data-testid="warning-event-block-future"
              style={{ width: `${100 - pastPercentage}%` }}
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                background,
              }}
            />
          </Box>
          {isEditable ? (
            <>
              <EdgeHandle edge="start" onBegin={beginDrag} />
              <EdgeHandle edge="end" onBegin={beginDrag} />
            </>
          ) : null}
          {isEditable && editing?.onLevelChange ? (
            <Box
              className="warning-event-block-kebab"
              onDoubleClick={(event): void => event.stopPropagation()}
              onPointerDown={(event): void => {
                kebabPressRef.current = { x: event.clientX, y: event.clientY };
              }}
              ref={kebabRef}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                visibility: isMenuOpen ? 'visible' : 'hidden',
                opacity: isMenuOpen ? 1 : 0.5,
                '&:hover': { opacity: 1 },
              }}
            >
              <CustomIconButton
                data-testid="warning-event-block-options"
                onClick={(event): void => {
                  // pointer clicks are handled on pointerup; this covers keyboard activation
                  if (event.detail === 0) {
                    setMenuAnchorEl(kebabRef.current);
                  }
                }}
                sx={{ backgroundColor: 'geowebColors.background.surface' }}
                variant="tool"
              >
                <Options />
              </CustomIconButton>
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={(): void => setMenuAnchorEl(null)}
        open={isMenuOpen}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <ListSubheader sx={{ fontSize: 12, lineHeight: '1.5' }}>
          {t('warning-level')}
        </ListSubheader>
        {levelOptions.map((option) => (
          <MenuItem
            key={option.key}
            onClick={(): void => {
              setMenuAnchorEl(null);
              editing?.onLevelChange?.(option.key as WarningLevel);
            }}
            sx={{
              '&.MuiMenuItem-root': {
                color: option.textColor,
                background: option.color,
                backgroundClip: 'content-box',
                padding: '12px 8px',
                '&:focus, &.Mui-focusVisible, &:hover': {
                  backgroundColor: option.color,
                  backgroundClip: 'content-box',
                },
              },
            }}
          >
            &nbsp;{t(option.translationKey)}
          </MenuItem>
        ))}
      </Menu>
      {isEditable && editing ? (
        <WarningEventTimelineBlockControls
          containerWidth={EDGE_PADDING * 2 + drawableWidth}
          editing={editing}
          from={from}
          left={left}
          maxPickerDate={absoluteMaxEnd}
          now={now}
          showEditAffordances={showEditAffordances}
          until={until}
          width={width}
        />
      ) : null}
    </>
  );
};
